// src/components/AttendanceCard.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  setDoc,
  serverTimestamp,
  Timestamp,
  GeoPoint,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/AttendanceCard.module.css';

/** Haversine in meters */
const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const getTodayDateString = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isiOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const AttendanceCard = ({ companyLocation }) => {
  const { currentUser } = useAuth();

  const [todaysAttendance, setTodaysAttendance] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [permState, setPermState] = useState(null); // 'granted' | 'prompt' | 'denied' | null
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  // Exceptions for today
  const [remoteAllowedToday, setRemoteAllowedToday] = useState(false);
  const [vacationToday, setVacationToday] = useState(false);
  const [activeExceptionId, setActiveExceptionId] = useState(null);

  const isMounted = useRef(true);

  // Allow overriding radius from Firestore settings: settings/companyProfile.officeLocation.radiusMeters
  const CHECKIN_RADIUS_METERS = useMemo(() => {
    const r = Number(companyLocation?.radiusMeters);
    return Number.isFinite(r) && r > 0 ? r : 200;
  }, [companyLocation]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Watch today's attendance doc
  useEffect(() => {
    if (!currentUser) return;
    const todayStr = getTodayDateString();
    const attendanceId = `${currentUser.uid}_${todayStr}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    const unsub = onSnapshot(attendanceRef, (snap) => {
      if (!isMounted.current) return;
      if (snap.exists()) {
        setTodaysAttendance({ id: snap.id, ...snap.data() });
      } else {
        setTodaysAttendance(null);
      }
    });
    return () => unsub();
  }, [currentUser]);

  // Listen for today's exception: remote or vacation
  useEffect(() => {
    if (!currentUser) return;

    const today = getTodayDateString();
    // Use one inequality on dateFrom and filter dateTo client-side for compatibility.
    const qEx = query(
      collection(db, 'attendanceExceptions'),
      where('userId', '==', currentUser.uid),
      where('dateFrom', '<=', today)
    );

    const unsub = onSnapshot(qEx, (snap) => {
      if (!isMounted.current) return;
      let remote = false;
      let vacation = false;
      let exId = null;

      snap.docs.forEach((d) => {
        const ex = d.data();
        // Guard: inclusive range match
        if (ex.dateTo && ex.dateTo >= today) {
          if (ex.type === 'remote') {
            remote = true;
            exId = d.id;
          } else if (ex.type === 'vacation') {
            vacation = true;
            exId = d.id;
          }
        }
      });

      // If both exist, vacation takes precedence.
      setVacationToday(vacation);
      setRemoteAllowedToday(!vacation && remote);
      setActiveExceptionId(exId);
    });

    return () => unsub();
  }, [currentUser]);

  // Best-effort read of Permissions API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await navigator.permissions?.query({ name: 'geolocation' });
        if (!cancelled && res) {
          setPermState(res.state);
          res.onchange = () => isMounted.current && setPermState(res.state);
        }
      } catch {
        // Not supported everywhere (notably iOS pre-16)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const errorHelpIfIOS = isiOS()
    ? `

On iPhone:
1) Open this site in Safari (not inside another app).
2) Settings → Privacy & Security → Location Services → Safari Websites:
   - Allow Location Access: While Using the App
   - Precise Location: ON
3) In Safari: tap “aA” → Website Settings → Location: Allow, then reload.
4) If installed as a Home Screen app: Settings → [Your App Name] → Location → While Using + Precise ON.
`
    : '';

  const tryGetCurrentPosition = (opts) =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, opts);
    });

  const saveCheckIn = async (position, distance, { usedRemoteFlow }) => {
    const { latitude, longitude, accuracy, altitude, heading, speed } =
      position?.coords || {};
    const todayStr = getTodayDateString();
    const attendanceId = `${currentUser.uid}_${todayStr}`;
    const ref = doc(db, 'attendance', attendanceId);

    await setDoc(
      ref,
      {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        date: todayStr,
        checkInTime: serverTimestamp(),
        status: 'Checked In',
        remoteAllowed: remoteAllowedToday || false,
        remoteCheckIn: usedRemoteFlow || false,
        exceptionId: activeExceptionId || null,
        ...(position
          ? {
              location: new GeoPoint(latitude, longitude),
              positionTimestamp: Timestamp.fromMillis(position.timestamp),
              accuracy: typeof accuracy === 'number' ? Math.round(accuracy) : null,
              altitude: typeof altitude === 'number' ? altitude : null,
              heading: typeof heading === 'number' ? heading : null,
              speed: typeof speed === 'number' ? speed : null,
              distanceFromOffice:
                typeof distance === 'number' ? Math.round(distance) : null,
              radiusUsed: CHECKIN_RADIUS_METERS,
            }
          : {}),
        ua: navigator.userAgent,
      },
      { merge: true }
    );
  };

  const saveCheckOut = async (position, distance) => {
    const { latitude, longitude, accuracy } = position?.coords || {};
    const todayStr = getTodayDateString();
    const attendanceId = `${currentUser.uid}_${todayStr}`;
    const ref = doc(db, 'attendance', attendanceId);

    await setDoc(
      ref,
      {
        checkOutTime: serverTimestamp(),
        status: 'Checked Out',
        autoCheckout: false,
        ...(position
          ? {
              checkOutLocation: new GeoPoint(latitude, longitude),
              checkOutAccuracy:
                typeof accuracy === 'number' ? Math.round(accuracy) : null,
              distanceFromOfficeAtCheckOut:
                typeof distance === 'number' ? Math.round(distance) : null,
            }
          : {}),
      },
      { merge: true }
    );
  };

  const handleCheckIn = async () => {
    if (!currentUser) {
      setMessage({ type: 'error', text: 'You must be logged in to check in.' });
      return;
    }
    if (vacationToday) {
      setMessage({
        type: 'error',
        text: 'You are on approved vacation today. Check-in is disabled.',
      });
      return;
    }
    if (!remoteAllowedToday && (!companyLocation?.latitude || !companyLocation?.longitude)) {
      setMessage({
        type: 'error',
        text: 'Office location is not configured. Please contact your administrator.',
      });
      return;
    }

    // Remote attendance path: geolocation optional, no geofence enforcement.
    if (remoteAllowedToday) {
      setIsProcessing(true);
      setMessage({ type: 'info', text: 'Checking you in (remote)…' });

      try {
        let position = null;
        if ('geolocation' in navigator) {
          try {
            // Try quick precise fix (optional)
            position = await tryGetCurrentPosition({
              enableHighAccuracy: true,
              timeout: 8000,
              maximumAge: 0,
            });
          } catch {
            // ignore; we still allow remote check-in without GPS
          }
        }

        let distance = null;
        if (position && companyLocation?.latitude) {
          const { latitude, longitude } = position.coords;
          distance = getDistanceInMeters(
            latitude,
            longitude,
            companyLocation.latitude,
            companyLocation.longitude
          );
        }

        await saveCheckIn(position, distance, { usedRemoteFlow: true });

        if (isMounted.current) {
          setMessage({ type: 'success', text: 'Remote check-in successful!' });
          setIsProcessing(false);
        }
      } catch (err) {
        console.error(err);
        if (isMounted.current) {
          setMessage({
            type: 'error',
            text: 'Could not complete remote check-in. Please try again.',
          });
          setIsProcessing(false);
        }
      }
      return;
    }

    // On-site geofenced path
    if (!('geolocation' in navigator)) {
      setMessage({
        type: 'error',
        text: 'Geolocation is not supported by your browser.',
      });
      return;
    }

    setIsProcessing(true);
    setMessage({ type: 'info', text: 'Requesting your precise location…' });

    const highOpts = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
    const lowOpts = { enableHighAccuracy: false, timeout: 20000, maximumAge: 0 };

    try {
      let position;
      try {
        position = await tryGetCurrentPosition(highOpts);
      } catch (e1) {
        if (e1?.code === 2 || e1?.code === 3) {
          position = await tryGetCurrentPosition(lowOpts);
        } else {
          throw e1;
        }
      }

      const { latitude, longitude, accuracy } = position.coords;
      const distance = getDistanceInMeters(
        latitude,
        longitude,
        companyLocation.latitude,
        companyLocation.longitude
      );

      const accuracyBuffer = Number.isFinite(accuracy) ? Math.max(accuracy, 30) : 50;
      const allowed = distance <= CHECKIN_RADIUS_METERS + accuracyBuffer;

      if (!allowed) {
        if (isMounted.current) {
          setMessage({
            type: 'error',
            text: `You appear too far from the office. Distance: ~${Math.round(
              distance
            )}m (GPS ±${Math.round(accuracy || 50)}m). Move closer and try again.`,
          });
          setIsProcessing(false);
        }
        return;
      }

      await saveCheckIn(position, distance, { usedRemoteFlow: false });

      if (isMounted.current) {
        setMessage({ type: 'success', text: 'Check-in successful!' });
        setIsProcessing(false);
      }
    } catch (error) {
      console.warn('Geolocation error:', error);
      let text = 'Could not get your location. Please ensure location is enabled.';
      if (error?.code === 1) {
        text =
          'Location access was denied. Please enable it in your browser/device settings.' +
          errorHelpIfIOS;
        setShowIOSHelp(true);
      } else if (error?.code === 2) {
        text = 'Location unavailable. Move near a window or go outside and try again.';
      } else if (error?.code === 3) {
        text = 'Timed out getting location. Try again and keep the app in the foreground.';
      }
      if (isMounted.current) {
        setMessage({ type: 'error', text });
        setIsProcessing(false);
      }
    }
  };

  const handleCheckOut = async () => {
    if (!currentUser) return;

    // If GPS is unavailable or denied, still allow checkout (record without position)
    if (!('geolocation' in navigator)) {
      await saveCheckOut(null, null);
      setMessage({ type: 'success', text: 'Checked out.' });
      return;
    }

    setIsProcessing(true);
    setMessage({ type: 'info', text: 'Getting your location for checkout…' });

    const highOpts = { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 };
    const lowOpts = { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 };

    try {
      let position;
      try {
        position = await tryGetCurrentPosition(highOpts);
      } catch (e1) {
        if (e1?.code === 2 || e1?.code === 3) {
          position = await tryGetCurrentPosition(lowOpts);
        } else {
          throw e1;
        }
      }

      const { latitude, longitude } = position.coords;
      const distance = companyLocation?.latitude
        ? getDistanceInMeters(
            latitude,
            longitude,
            companyLocation.latitude,
            companyLocation.longitude
          )
        : null;

      await saveCheckOut(position, distance);

      if (isMounted.current) {
        setMessage({ type: 'success', text: 'Checked out. Have a great rest of your day!' });
        setIsProcessing(false);
      }
    } catch (error) {
      console.warn('Checkout geolocation error:', error);
      // If user declines GPS at checkout time, still let them checkout
      await saveCheckOut(null, null);
      if (isMounted.current) {
        setMessage({
          type: 'success',
          text:
            'Checked out (without location). To include your location next time, allow location access.',
        });
        setIsProcessing(false);
      }
    }
  };

  // ===== Derived UI state =====

  const checkedInAt =
    todaysAttendance?.checkInTime?.toDate &&
    todaysAttendance.checkInTime.toDate().toLocaleTimeString();

  const checkedOutAt =
    todaysAttendance?.checkOutTime?.toDate &&
    todaysAttendance.checkOutTime.toDate().toLocaleTimeString();

  const isCheckedIn = Boolean(todaysAttendance?.checkInTime) && !todaysAttendance?.checkOutTime;
  const isCheckedOut = Boolean(todaysAttendance?.checkOutTime);

  return (
    <div className={styles.attendanceCard}>
      <div className={styles.headerRow}>
        <h4>Today&apos;s Attendance</h4>
        <div className={styles.headerBadges}>
          {vacationToday && <span className={styles.permPrompt} style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid #f59e0b', color: '#fbbf24' }}>Vacation</span>}
          {remoteAllowedToday && !vacationToday && (
            <span className={styles.permGranted} style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid #10b981', color: '#34d399' }}>
              Remote Allowed
            </span>
          )}
          {permState && (
            <span
              className={`${styles.permBadge} ${
                permState === 'granted'
                  ? styles.permGranted
                  : permState === 'prompt'
                  ? styles.permPrompt
                  : styles.permDenied
              }`}
              title={`Geolocation permission: ${permState}`}
            >
              {permState}
            </span>
          )}
        </div>
      </div>

      {/* Status line */}
      <p className={styles.statusText}>
        {todaysAttendance
          ? isCheckedOut
            ? `Checked in at ${checkedInAt} · Checked out at ${checkedOutAt}`
            : `Checked in at ${checkedInAt} · Not checked out yet`
          : vacationToday
          ? 'Vacation today (approved).'
          : remoteAllowedToday
          ? 'Remote attendance is allowed today.'
          : 'You are not checked in for today.'}
      </p>

      {/* Buttons */}
      {!todaysAttendance && !vacationToday && (
        <button
          onClick={handleCheckIn}
          className={styles.checkInButton}
          disabled={isProcessing}
          title={remoteAllowedToday ? 'Remote check-in enabled (no geofence)' : undefined}
        >
          {isProcessing ? 'Checking…' : remoteAllowedToday ? 'Check In (Remote)' : 'Check In'}
        </button>
      )}

      {isCheckedIn && (
        <button onClick={handleCheckOut} className={styles.checkOutButton} disabled={isProcessing}>
          {isProcessing ? 'Checking out…' : 'Check Out'}
        </button>
      )}

      {/* iOS help */}
      {showIOSHelp && isiOS() && (
        <details className={styles.iosHelp} open>
          <summary>How to enable location on iPhone</summary>
          <ol>
            <li>Open this site in <strong>Safari</strong> (not inside another app).</li>
            <li>
              Settings → <strong>Privacy &amp; Security</strong> → <strong>Location Services</strong> → <strong>Safari
              Websites</strong>.
            </li>
            <li>
              Set <em>Allow Location Access</em> to <strong>While Using the App</strong> and turn <strong>Precise
              Location</strong> ON.
            </li>
            <li>
              In Safari: tap <strong>aA</strong> → <strong>Website Settings</strong> → <strong>Location: Allow</strong>,
              then reload.
            </li>
            <li>
              If installed to Home Screen: Settings → <strong>Your App Name</strong> → <strong>Location</strong> →{' '}
              <em>While Using the App</em> + <strong>Precise</strong> ON.
            </li>
          </ol>
        </details>
      )}

      {/* Messages */}
      {message.text && (
        <p className={`${styles.message} ${styles[message.type]}`}>{message.text}</p>
      )}

      {/* Meta */}
      <div className={styles.metaRow}>
        <small>
          {remoteAllowedToday && !vacationToday ? (
            <>
              Remote attendance active today.
              {companyLocation?.name ? <> &nbsp;· Office: {companyLocation.name}</> : null}
            </>
          ) : (
            <>
              Required radius:&nbsp;<strong>{CHECKIN_RADIUS_METERS}m</strong>
              {companyLocation?.name ? <> &nbsp;· Office: {companyLocation.name}</> : null}
            </>
          )}
          {activeExceptionId ? <> &nbsp;· Exception ID: {activeExceptionId}</> : null}
        </small>
      </div>
    </div>
  );
};

export default AttendanceCard;
